/* eslint-disable react-hooks/rules-of-hooks */
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { convertToDisplayName } from '../helpers';
import { GenericFormComponentProps } from '../types';
import formInputStyles from '../shared.module.scss';
import { useFormSectionContext } from '../FormSection/FormSection';
import { useFormState } from 'hooks/useFormState';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './FormText.module.scss';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface FormTextProps extends GenericFormComponentProps {
    isNumber?: boolean;
    isTextArea?: boolean;
    textSecurity?: boolean;
}

/**
 * simple form text input
 * @param {string} field - field to track with react-hook-form
 * @param {string} label - optional label override
 * @param {boolean} isNumber - boolean to track a number instead of text field
 * @param {boolean} isTextArea - boolean to track a a textarea instead of a generic text input
 * @param {boolean} disabled - is this component readonly?
 * @param {string} className - for styling
 * @returns FormText component
 */
export function FormText({
    field,
    label,
    isNumber,
    isTextArea = false,
    disabled,
    className,
    onChange,
    textSecurity,
}: FormTextProps) {
    const { control, getFieldState, setValue } = useFormContext();
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();
    const [security, setSecurity] = useState(true);

    const watchedField = getFieldState(field);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: { name: field, error: !!watchedField.error },
        });
    }, [field, formSection, setFormSectionError, watchedField.error]);

    label = label || convertToDisplayName(field);

    const handleOnChange = useCallback(
        (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            onChange: (...event: any[]) => void
        ) => {
            const isMoreThanHundredths = e.target.value.match(/\.\d{3}/g);
            if (isNumber) {
                if (!e.target.value) {
                    return setValue(field, '');
                }
                if (isMoreThanHundredths) {
                    e.target.value = parseFloat(e.target.value).toFixed(2);
                }
                onChange(e);
                return setValue(field, +e.target.value);
            }
            return onChange(e);
        },
        [field, isNumber, setValue]
    );

    // we memoize this component so it does not change after every rerender of the useEffect hook. MUI's multiline component conflicts here
    return useMemo(() => {
        return (
            <Controller
                name={field}
                control={control}
                render={({ field: fieldProps, fieldState: { error } }) => {
                    const id = `text-field-${field}`;

                    return (
                        <TextField
                            id={id}
                            variant="standard"
                            type={
                                security && textSecurity
                                    ? 'password'
                                    : isNumber
                                    ? 'number'
                                    : 'text'
                            }
                            multiline={isTextArea}
                            maxRows={4}
                            label={label}
                            color={error?.message ? 'error' : 'primary'}
                            inputProps={{
                                ...fieldProps,
                                value: fieldProps.value ?? '',
                                inputMode: isNumber ? 'decimal' : 'text',
                                pattern: isNumber ? '[0-9]*' : '',
                                onChange: (
                                    e: React.ChangeEvent<
                                        HTMLInputElement | HTMLTextAreaElement
                                    >
                                ) => handleOnChange(e, fieldProps.onChange),
                                onBlur:
                                    isNumber && !fieldProps.value
                                        ? () => setValue(field, 0)
                                        : undefined,
                            }}
                            InputProps={{
                                endAdornment: textSecurity ? (
                                    <InputAdornment position="start">
                                        <Tooltip title="Show">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() =>
                                                    setSecurity(!security)
                                                }
                                                size="small"
                                                edge="end"
                                                className={
                                                    styles.visibilityButton
                                                }
                                            >
                                                {security ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                            className={`${formInputStyles.formInput} ${className}`}
                            disabled={disabled}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    );
                }}
            />
        );
    }, [
        field,
        control,
        isNumber,
        isTextArea,
        label,
        className,
        textSecurity,
        security,
        disabled,
        onChange,
        handleOnChange,
        setValue,
    ]);
}
